import Dropdown, { type DropdownOption } from '../ui/Dropdown';
import Card from '../ui/Card';

type PreferenceBase = {
  id: string;
  label: string;
  description: string;
  /** Turn on when this choice can be stored. Until then the control stays inactive. */
  enabled: boolean;
  unavailableNote: string;
};

type TogglePreference = PreferenceBase & {
  control: 'toggle';
};

type ChoicePreference = PreferenceBase & {
  control: 'choice';
  options: DropdownOption[];
  value: string;
  placeholder: string;
};

const LANGUAGE_PREFERENCE: ChoicePreference = {
  id: 'language',
  label: 'Language',
  description: 'English or French for site menus and buttons.',
  enabled: false,
  unavailableNote: 'Not available yet. French site text is still to come.',
  control: 'choice',
  options: [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'French' },
  ],
  value: 'en',
  placeholder: 'English',
};

const PRIMARY_HOSPITAL_PREFERENCE: ChoicePreference = {
  id: 'primaryHospital',
  label: 'Primary hospital',
  description: 'The site used for hospital tools.',
  enabled: false,
  unavailableNote: 'Not available yet. Hospital tools are still to come.',
  control: 'choice',
  options: [],
  value: '',
  placeholder: 'Choose a hospital',
};

const EMAIL_PREFERENCES: TogglePreference[] = [
  {
    id: 'savedResourceUpdates',
    label: 'Saved resources',
    description: 'Email me when a saved resource is updated.',
    enabled: false,
    unavailableNote: 'Not available yet. These emails are not sent.',
    control: 'toggle',
  },
  {
    id: 'followedTopics',
    label: 'Followed topics',
    description: 'Email me when something new matches my topics.',
    enabled: false,
    unavailableNote: 'Not available yet. These emails are not sent.',
    control: 'toggle',
  },
  {
    id: 'eventReminders',
    label: 'Event reminders',
    description: 'Email me a reminder for events I register for.',
    enabled: false,
    unavailableNote: 'Not available yet. Event registration is still to come.',
    control: 'toggle',
  },
  {
    id: 'courseReminders',
    label: 'Courses in progress',
    description: 'Email me if a course I started is still unfinished.',
    enabled: false,
    unavailableNote: 'Not available yet. These emails are not sent.',
    control: 'toggle',
  },
  {
    id: 'weeklyDigest',
    label: 'Weekly digest',
    description: 'Email me a weekly summary of new material.',
    enabled: false,
    unavailableNote: 'Not available yet. A digest is not sent.',
    control: 'toggle',
  },
];

function PreferenceNote({ children }: { children: string }) {
  return <p className="text-xs text-gray-500 mt-1">{children}</p>;
}

function ToggleRow({ item }: { item: TogglePreference }) {
  return (
    <div className="flex items-center justify-between gap-4 min-h-[44px] py-2">
      <div>
        <p className="text-sm font-medium text-gray-700">{item.label}</p>
        <p className="text-xs text-gray-500">{item.description}</p>
        {!item.enabled && <PreferenceNote>{item.unavailableNote}</PreferenceNote>}
      </div>
      <input
        type="checkbox"
        checked={false}
        disabled={!item.enabled}
        aria-label={item.label}
        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
        onChange={() => undefined}
      />
    </div>
  );
}

function ChoiceRow({ item }: { item: ChoicePreference }) {
  return (
    <div className="py-2">
      <Dropdown
        label={item.label}
        options={item.options}
        value={item.value}
        placeholder={item.placeholder}
        disabled={!item.enabled}
        onChange={() => undefined}
      />
      <p className="text-xs text-gray-500 mt-1">{item.description}</p>
      {!item.enabled && <PreferenceNote>{item.unavailableNote}</PreferenceNote>}
    </div>
  );
}

export default function AccountPreferences({
  hospitalOptions,
}: {
  hospitalOptions: DropdownOption[];
}) {
  const primaryHospital: ChoicePreference = {
    ...PRIMARY_HOSPITAL_PREFERENCE,
    options:
      hospitalOptions.length > 0
        ? hospitalOptions
        : [{ value: 'none', label: 'Choose a hospital' }],
  };

  return (
    <section id="preferences" className="scroll-mt-24 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
        <p className="text-sm text-gray-600 mt-1">
          These choices stay off until each feature is available. Nothing in this section is saved.
        </p>
      </div>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Site</h3>
        <div className="space-y-2">
          <ChoiceRow item={LANGUAGE_PREFERENCE} />
          <ChoiceRow item={primaryHospital} />
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Email</h3>
        <div className="divide-y divide-gray-100">
          {EMAIL_PREFERENCES.map((item) => (
            <ToggleRow key={item.id} item={item} />
          ))}
        </div>
      </Card>
    </section>
  );
}
